import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';

interface RouterContextType {
    path: string;
    navigate: (to: string) => void;
}

const RouterContext = createContext<RouterContextType | null>(null);

const isRestrictedHistoryOrigin = () => {
    try {
        return window.location.protocol === 'blob:' || window.location.origin === 'null';
    } catch {
        return true;
    }
};

// Resolve current route from clean HTML5 pathname first, with fallback for legacy #/hash URLs
const getCurrentPath = (): string => {
    const hash = window.location.hash;
    // Support legacy hash routes like #/app or #/blog/...
    if (hash && hash.startsWith('#/')) {
        return hash.substring(1);
    }

    let currentPathname = window.location.pathname || '/';
    if (currentPathname.startsWith(window.location.origin)) {
        currentPathname = currentPathname.substring(window.location.origin.length);
    }
    if (!currentPathname || currentPathname === '/index.html') {
        currentPathname = '/';
    }

    return currentPathname + (window.location.search || '');
};

export function Router({ children }: { children: React.ReactNode }) {
    const [path, setPath] = useState<string>(getCurrentPath);

    const syncLocation = useCallback(() => {
        // If user arrived via a legacy #/route and history API is supported, upgrade to clean path for SEO
        if (window.location.hash.startsWith('#/') && !isRestrictedHistoryOrigin()) {
            const cleanTarget = window.location.hash.substring(1);
            try {
                window.history.replaceState(null, '', cleanTarget);
            } catch {
                // Ignore in restricted iframe contexts
            }
        }
        setPath(getCurrentPath());
    }, []);

    useEffect(() => {
        syncLocation();
        window.addEventListener('popstate', syncLocation);
        window.addEventListener('hashchange', syncLocation);
        return () => {
            window.removeEventListener('popstate', syncLocation);
            window.removeEventListener('hashchange', syncLocation);
        };
    }, [syncLocation]);

    const navigate = useCallback((to: string) => {
        if (!to) return;

        // Handle in-page anchor jumps (e.g., #features)
        if (to.startsWith('#') && !to.startsWith('#/')) {
            window.location.hash = to;
            return;
        }

        const normalizedTarget = to.startsWith('/') ? to : `/${to}`;

        if (!isRestrictedHistoryOrigin()) {
            try {
                window.history.pushState(null, '', normalizedTarget);
                setPath(normalizedTarget);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                return;
            } catch {
                // Fallback to hash routing if pushState is blocked
            }
        }

        window.location.hash = normalizedTarget;
        setPath(normalizedTarget);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    return (
        <RouterContext.Provider value={{ path, navigate }}>
            {children}
        </RouterContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export const useRouter = () => {
    const context = useContext(RouterContext);
    if (!context) {
        throw new Error('useRouter must be used within a Router');
    }
    return context;
};